'use client';

import { useProjectResponseModeContext } from '@/shared-survey';
import type { GetProjectQuery } from '@/survey-graphql';

import SurveyAiFormsAiSectionMatrixQuestionRow, {
  type SortedOption,
} from './survey-ai-forms-ai-section-matrix-question-row';
import { useMatrixQuestion } from './survey-ai-forms-ai-section-matrix-question.hooks';
import SurveyAiFormsAiSectionQuestionTitle from './survey-ai-forms-ai-section-question-title';
import SurveyAiFormsInputComposer from './survey-ai-forms-input-composer';
import type { FormAnswerValue, ResponseMode } from './survey-ai-forms.types';

type ProjectQuestion = NonNullable<
  GetProjectQuery['project']
>['questions'][number];

function sortRatingOptions(
  options: ProjectQuestion['options'] | undefined
): SortedOption[] {
  return (options ?? [])
    .filter((o): o is NonNullable<typeof o> => o != null)
    .toSorted((a, b) => +a.value - +b.value)
    .map((o) => ({ label: o.label, value: o.value }));
}

interface Props {
  question: ProjectQuestion;
  value: FormAnswerValue | undefined;
  transcriptValue: string;
  onChange: (value: FormAnswerValue) => void;
  onTranscriptChange: (value: string) => void;
  onSubmit: () => void;
  onNext?: () => void;
  hasValue: boolean;
  responseMode: ResponseMode;
  loading?: boolean;
  isMobile?: boolean;
  isUserVoiceEnabled?: boolean;
}

export default function SurveyAiFormsAiSectionMatrixQuestion({
  question,
  value,
  transcriptValue,
  onChange,
  onTranscriptChange,
  onSubmit,
  onNext,
  hasValue,
  responseMode,
  loading = false,
  isMobile = false,
  isUserVoiceEnabled = false,
}: Props) {
  const { isListening } = useProjectResponseModeContext();

  const { rows, naEnabled, naLabel, handleSelect, handleToggleNa } =
    useMatrixQuestion({ question, value, onChange });

  const options = sortRatingOptions(question.options);
  const lowLabel = options[0]?.label ?? '';
  const highLabel = options[options.length - 1]?.label ?? '';

  const handleComposerSubmit = onNext ?? onSubmit;

  return (
    <div data-test='matrix-question' className='flex w-full flex-col gap-8'>
      <SurveyAiFormsAiSectionQuestionTitle
        text={question.text}
        assets={question.assets}
      />

      <div className='flex w-full flex-col gap-4'>
        {!isMobile && (
          <div
            data-test='matrix-header'
            className='flex w-full items-start gap-8'
          >
            <div className='w-80 shrink-0' aria-hidden='true' />
            <div className='flex flex-1 items-center gap-4'>
              <div className='flex flex-1 items-center justify-between [font-family:var(--brand-font)] text-xs/4.5 font-semibold text-(--brand-secondary)'>
                <span data-test='matrix-low-label'>{lowLabel}</span>
                <span data-test='matrix-high-label' className='text-right'>
                  {highLabel}
                </span>
              </div>
              {naEnabled && (
                <span
                  data-test='matrix-na-header'
                  className='w-8 shrink-0 text-right [font-family:var(--brand-font)] text-xs/4.5 font-semibold text-(--brand-secondary)'
                >
                  {naLabel}
                </span>
              )}
            </div>
          </div>
        )}

        <div className='flex w-full flex-col gap-4'>
          {rows.map((row) => (
            <SurveyAiFormsAiSectionMatrixQuestionRow
              key={row.id}
              row={row}
              options={options}
              naEnabled={naEnabled}
              onSelect={handleSelect}
              onToggleNa={handleToggleNa}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>

      <SurveyAiFormsInputComposer
        value={transcriptValue}
        onChange={onTranscriptChange}
        onSubmit={handleComposerSubmit}
        responseMode={responseMode}
        instanceId={question.id}
        canSubmit={hasValue}
        isListening={isListening}
        questionType={question.type}
        loading={loading}
        isMobile={isMobile}
        isUserVoiceEnabled={isUserVoiceEnabled}
      />
    </div>
  );
}
